import React, { useState, useReducer } from 'react';
import { connect } from 'react-redux';
import Geohash from 'latlon-geohash';
import { func, arrayOf, object, number } from 'prop-types';
import AppsIcon from '@mui/icons-material/Apps';
import { mannaApiHost } from '../../constants';

import {
  Grid,
  Chip,
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Checkbox,
  Typography
} from '@mui/material';

import {
  red,
  pink,
  indigo,
  blue,
  cyan,
  lime,
  purple,
  teal,
  green,
  yellow,
  orange
} from '@mui/material/colors';

import Marker from 'react-leaflet-enhanced-marker';
import { MapContainer, TileLayer, FeatureGroup, Rectangle, Circle, Popup, Tooltip } from 'react-leaflet';
import copy from 'clipboard-copy';

import Teleport from 'components/Teleport';
import FlightIcon from 'components/FlightIcon';

// Actions
import {
  addGeohashGroup,
  addTupleList,
  removeGeohashGroup,
  createMission
} from 'actions/toolsActions';

import { dublinCoords } from '../../constants';
import { buildGeoJson } from './tools';
import Page from '../../components/Page/Page';

const Tools = props => {
  const initialLocation = dublinCoords;
  const [center, setCenter] = useState(dublinCoords);
  const [name, setName] = useState('');
  const [list, setList] = useState('');
  const [tupleList, setTupleList] = useState('');
  const [singleGeohash, setSingleGeohash] = useState('');
  const [marker, setMarker] = useState(initialLocation);
  const [radius, setRadius] = useState(0);
  const [basket, setBasket] = useState('');
  const [speed, setSpeed] = useState(0);
  const [altitude, setAltitude] = useState(0);
  const [showLabels, setShowLabels] = useState(true);
  const [showInspector, setShowInspector] = useState(false);
  const [showFlightPlanner, setShowFlightPlanner] = useState(true);
  const [missionPlanning, setMissionPlanning] = useState(false);
  const [heightFromGround, setHeightFromGround] = useState(38);
  const [cuboidHeight, setCuboidHeight] = useState(38);
  const [cameraFlyTo, setCameraFlyTo] = useState(false);

  const initialState = {
    points: []
  };

  const ADD_POINT = 'ADD_POINT';
  const RESET_STATE = 'RESET_STATE';
  const SET_DROP = 'SET_DROP';

  function reducer(state, action) {
    let type;
    let newPoints;

    switch (action.type) {
      case ADD_POINT:
        if (state.points.length === 0) {
          type = 'startpoint';
        } else {
          type = 'endpoint';
        }

        newPoints = state.points.map(point => {
          if (point.type === 'endpoint') {
            point.type = 'point';
          }

          return point;
        });

        return {
          ...state,
          points: [
            ...newPoints,
            {
              type,
              coordinates: action.payload
            }
          ]
        };

      case SET_DROP:
        return {
          ...state,
          points: state.points.map((point, index) => {
            if (index === action.payload) {
              point.type = 'drop';
            }
            return point;
          })
        };

      case RESET_STATE:
        return initialState;

      default:
        throw new Error();
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    geohashGroups,
    nextColorIndex,
    tupleLists,
    doAddGeohashGroup,
    doAddTupleList,
    doRemoveGeohashGroup,
    doCreateMission
  } = props;

  const refs = {};
  const mapRef = React.createRef();

  const availableColors = [
    red[500],
    indigo[500],
    green[500],
    purple[500],
    orange[500],
    teal[500],
    pink[500],
    blue[500],
    cyan[500],
    lime[500],
    yellow[500]
  ];

  return (
    <Page style={{ padding: '24px' }}>
      <Grid container spacing={3}>
        <Grid item xs={8}>
          <Card>
            <CardContent>
              <MapContainer
                center={initialLocation}
                style={{ height: '500px' }}
                zoom="16"
                ref={mapRef}
                onmoveend={event => {
                  const mapCenter = event.target.getCenter();
                  setCenter([mapCenter.lat, mapCenter.lng]);
                }}
                onclick={e => {
                  if (missionPlanning) {
                    dispatch({
                      type: ADD_POINT,
                      payload: e.latlng
                    });
                  }
                }}
              >
                <TileLayer
                  attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />

                {geohashGroups.map(group => {
                  refs[group.id] = React.createRef();

                  return (
                    <FeatureGroup ref={refs[group.id]}>
                      {group.list.map(geohash => {
                        const geoBounds = Geohash.bounds(geohash);
                        const bounds = [geoBounds.sw, geoBounds.ne];
                        return (
                          <Rectangle
                            bounds={bounds}
                            color={group.color}
                            opacity={0.4}
                            fillOpacity={0.1}
                            key={geohash}
                          >
                            {showLabels && (
                              <Tooltip permanent direction="center">
                                {geohash}
                              </Tooltip>
                            )}
                          </Rectangle>
                        );
                      })}
                    </FeatureGroup>
                  );
                })}

                {tupleLists.map(group => {
                  refs[group.id] = React.createRef();

                  return (
                    <FeatureGroup ref={refs[group.id]}>
                      {group.tupleList.map(tuple => {
                        const geohash = tuple.split(':')[0];
                        const geoBounds = Geohash.bounds(geohash);
                        const bounds = [geoBounds.sw, geoBounds.ne];
                        return (
                          <Rectangle
                            bounds={bounds}
                            color={group.color}
                            opacity={0.4}
                            fillOpacity={0.1}
                            key={geohash}
                          >
                            {showLabels && (
                              <Tooltip permanent direction="center">
                                {geohash}
                              </Tooltip>
                            )}
                          </Rectangle>
                        );
                      })}
                    </FeatureGroup>
                  );
                })}

                {showInspector && (
                  <>
                    <Marker
                      position={center}
                      draggable
                      onMove={e => setMarker(e.latlng)}
                    >
                      <Popup maxWidth={600}>{`${marker.lat}, ${marker.lng}`}</Popup>
                    </Marker>
                    <Circle center={marker} radius={radius} />
                  </>
                )}

                {state.points &&
                  state.points.map((point, index) => (
                    <Marker
                      icon={<FlightIcon variant={point.type} color="yellow" />}
                      position={point.coordinates}
                    >
                      <Popup>
                        <Button
                          onClick={() =>
                            dispatch({
                              type: SET_DROP,
                              payload: index
                            })
                          }
                        >
                          Set Drop
                        </Button>
                      </Popup>
                    </Marker>
                  ))}
              </MapContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Teleport" />
                <Divider />
                <CardContent>
                  <Teleport ref={mapRef} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Inspector marker"
                  action={
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showInspector}
                          onChange={() => setShowInspector(!showInspector)}
                          value="showLabels"
                        />
                      }
                      label="Show inspector market"
                    />
                  }
                />
                <Divider />
                <CardContent>
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="Radius (m)"
                    onChange={e => setRadius(e.target.value)}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Flight Planner"
                  action={
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showFlightPlanner}
                          onChange={() =>
                            setShowFlightPlanner(!showFlightPlanner)
                          }
                          value="showFlightPlanner"
                        />
                      }
                      label="Show flight planner"
                    />
                  }
                />
                <Divider />
                <CardContent>
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="Relative Altitude"
                    value={altitude || ''}
                    onChange={e => setAltitude(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="Speed"
                    value={speed || ''}
                    onChange={e => setSpeed(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="Basket"
                    value={basket || ''}
                    onChange={e => setBasket(e.target.value)}
                    fullWidth
                  />
                  <Button
                    onClick={() => {
                      setMissionPlanning(!missionPlanning);
                      if (missionPlanning) {
                        dispatch({ type: RESET_STATE });
                      }
                    }}
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    variant="contained"
                    color="primary"
                  >
                    {missionPlanning ? 'Reset Route' : 'Plan Route'}
                  </Button>
                  <Button
                    onClick={() => {
                      copy(
                        JSON.stringify(
                          buildGeoJson(state.points, altitude, speed)
                        )
                      );
                    }}
                  >
                    Copy to clipboard
                  </Button>
                  <Button
                    onClick={() =>
                      doCreateMission(
                        basket,
                        buildGeoJson(state.points, altitude, speed)
                      )
                    }
                  >
                    Create Mission
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Geohash Groups"
                  action={
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showLabels}
                          onChange={() => setShowLabels(!showLabels)}
                          value="showLabels"
                        />
                      }
                      label="Show labels"
                    />
                  }
                />
                <Divider />
                <CardContent>
                  {geohashGroups.map(group => (
                    <Chip
                      icon={<AppsIcon />}
                      label={group.name}
                      onClick={() => {
                        const map = mapRef.current.leafletElement;
                        const bounds = refs[
                          group.id
                        ].current.leafletElement.getBounds();
                        map.fitBounds(bounds);
                        setCameraFlyTo([
                          bounds._northEast.lng,
                          bounds._northEast.lat,
                          600
                        ]);
                      }}
                      onDelete={() => doRemoveGeohashGroup(group.id)}
                    />
                  ))}
                  {tupleLists.map(group => (
                    <Chip
                      icon={<AppsIcon />}
                      label={group.name}
                      onClick={() => {
                        const map = mapRef.current.leafletElement;
                        const bounds = refs[
                          group.id
                        ].current.leafletElement.getBounds();
                        map.fitBounds(bounds);
                        setCameraFlyTo([
                          bounds._northEast.lng,
                          bounds._northEast.lat,
                          600
                        ]);
                      }}
                      onDelete={() => doRemoveGeohashGroup(group.id)}
                    />
                  ))}
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="Group name"
                    value={name || ''}
                    onChange={e => setName(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="Geohash list"
                    multiline
                    placeholder="Comma separated"
                    value={list || ''}
                    onChange={e => setList(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="Geohash cube tuples list"
                    multiline
                    placeholder="Format: [geohash]:[altitude]:[cube height], ..."
                    value={tupleList || ''}
                    onChange={e => setTupleList(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="Geohash breakdown"
                    multiline
                    value={singleGeohash || ''}
                    onChange={e => setSingleGeohash(e.target.value)}
                    fullWidth
                  />
                  <Divider />
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="3D: height from ground"
                    multiline
                    value={heightFromGround || ''}
                    onChange={e => setHeightFromGround(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    label="3D: cuboid height"
                    multiline
                    value={cuboidHeight || ''}
                    onChange={e => setCuboidHeight(e.target.value)}
                    fullWidth
                  />
                  <Button
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      const colorIndex =
                        nextColorIndex > availableColors.length
                          ? (nextColorIndex % availableColors.length) - 1
                          : nextColorIndex;

                      const color = availableColors[colorIndex];

                      if (list)
                        doAddGeohashGroup(
                          name,
                          list,
                          color,
                          heightFromGround,
                          cuboidHeight
                        );
                      if (tupleList) doAddTupleList(name, tupleList);
                      if (singleGeohash) {
                        const base32 = '0123456789bcdefghjkmnpqrstuvwxyz';
                        const breakdown = Array.prototype.map
                          .call(base32, char => singleGeohash + char)
                          .join(',');
                        doAddGeohashGroup(
                          `${singleGeohash} breakdown`,
                          breakdown,
                          color,
                          heightFromGround,
                          cuboidHeight
                        );
                      }
                      setName('');
                      setList('');
                      setTupleList('');
                    }}
                  >
                    Add
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader title="Environment" />
                <CardContent>
                  <Typography variant="body1">{`API host: ${mannaApiHost}`}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Page>
  );
};

const mapStateToProps = state => ({
  geohashGroups: state.tools.geohashGroups,
  tupleLists: state.tools.tupleLists,
  nextColorIndex: state.tools.nextColorIndex
});

const mapDispatchToProps = dispatch => ({
  doAddGeohashGroup: (name, list, color, heightFromGround, cuboidHeight) =>
    dispatch(
      addGeohashGroup(name, list, color, heightFromGround, cuboidHeight)
    ),
  doAddTupleList: (name, tupleList) => dispatch(addTupleList(name, tupleList)),
  doRemoveGeohashGroup: id => dispatch(removeGeohashGroup(id)),
  doCreateMission: (basket, geoJson) => dispatch(createMission(basket, geoJson))
});

Tools.propTypes = {
  geohashGroups: arrayOf(object),
  tupleLists: arrayOf(object),
  nextColorIndex: number,
  doAddGeohashGroup: func.isRequired,
  doAddTupleList: func.isRequired,
  doRemoveGeohashGroup: func.isRequired,
  doCreateMission: func.isRequired
};

Tools.defaultProps = {
  geohashGroups: [],
  nextColorIndex: 0
};

export default connect(mapStateToProps, mapDispatchToProps)(Tools);
